import React, { createContext, useContext, type ReactNode } from 'react';

/**
 * Tracks whether an ancestor is already choreographing this subtree.
 *
 * The Post Office message wrapper (and any other outer choreographer) applies
 * choreography once, at the top of a message. A choreographer-wrapped component
 * rendered further down — e.g. a product spotlight such as `JiraSpotlight`, or a
 * Confluence component built on `@confluence/choreographer-services` — would
 * otherwise start a *second*, independent choreographer over the same subtree.
 * Two choreographers gating the same DOM collide and the message can silently
 * fail to render.
 *
 * When this context is `true`, choreographer-wrapped components skip their own
 * choreography and render their children directly, deferring to the ancestor.
 */
const IsChoreographedByAncestorContext = createContext<boolean>(false);

/**
 * Marks the subtree as already choreographed by an ancestor. Wrap the rendered
 * message UI with this so nested choreographer-wrapped components defer instead
 * of double-choreographing.
 */
export const AncestorChoreographerProvider = ({
	children,
}: {
	children: ReactNode;
}): JSX.Element => (
	<IsChoreographedByAncestorContext.Provider value={true}>
		{children}
	</IsChoreographedByAncestorContext.Provider>
);

/** `true` when an ancestor is already choreographing this subtree. */
export const useIsChoreographedByAncestor = (): boolean =>
	useContext(IsChoreographedByAncestorContext);
